import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, Heart, Shield, Trash2, Key } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { WebAuthnUtils } from '../lib/webauthn-utils'
import { toast } from 'sonner'
import PasskeyCompatibilityChecker from '../components/PasskeyCompatibilityChecker'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false)
  const [isPasskeySupported, setIsPasskeySupported] = useState(false)
  
  const { signIn, signInWithPasskey, isPasskeySupported: checkPasskeySupport } = useAuth()
  const navigate = useNavigate()

  // Verificar suporte a passkeys
  useEffect(() => {
    const checkSupport = async () => {
      const supported = await checkPasskeySupport()
      setIsPasskeySupported(supported)
    }
    checkSupport()
  }, [checkPasskeySupport])

  const handleClearStorage = () => {
    try {
      localStorage.clear()
      sessionStorage.clear()
      toast.success('Armazenamento local limpo com sucesso!')
      // Recarregar a página para garantir estado limpo
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      console.error('Erro ao limpar armazenamento:', error)
      toast.error('Erro ao limpar armazenamento local')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) {
      toast.error('Por favor, preencha todos os campos')
      return
    }

    setIsLoading(true)
    
    try {
      const result = await signIn(email, password)
      
      if (result.error) {
        toast.error(result.error)
      } else {
        // O redirecionamento será feito automaticamente pelo PublicRoute
        // baseado no role do usuário após o login
        toast.success('Login realizado com sucesso!')
      }
    } catch (error) {
      console.error('Erro no login:', error)
      toast.error('Erro inesperado durante o login')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasskeyLogin = async () => {
    setIsPasskeyLoading(true)
    try {
      // Verificar se WebAuthn é suportado
      if (!WebAuthnUtils.WebAuthnDetector.isSupported()) {
        toast.error('WebAuthn não é suportado neste navegador')
        return
      }

      const result = await signInWithPasskey()
      if (result.error) {
        toast.error(result.error)
      } else {
        toast.success('Login com passkey realizado com sucesso!')
        navigate('/dashboard')
      }
    } catch (error: unknown) {
      console.error('Erro no login com passkey:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      toast.error(errorMessage || 'Erro inesperado durante o login com passkey')
    } finally {
      setIsPasskeyLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center items-center space-x-2 mb-4">
            <div className="bg-blue-600 p-3 rounded-xl">
              <Heart className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Saúde Virtual</h1>
          </div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Acesse sua conta
          </h2>
          <p className="text-gray-500">
            Sistema seguro de registros médicos com blockchain
          </p>
        </div>

        {/* Security Badge */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">
              Protegido com Criptografia Pós-Quântica
            </span>
          </div>
        </div>

        {/* Login Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Sua senha"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Entrando...</span>
              </div>
            ) : (
              'Entrar'
            )}
          </button>

          {/* Passkey Login Button */}
          {isPasskeySupported && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">ou</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handlePasskeyLogin}
                disabled={isPasskeyLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Entrar com passkey"
              >
                {isPasskeyLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    <span>Autenticando...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Key className="h-4 w-4" />
                    <span>Entrar com Passkey</span>
                  </div>
                )}
              </button>
            </>
          )}

          {/* Passkey Compatibility Info */}
          <PasskeyCompatibilityChecker
            onSupportChange={setIsPasskeySupported}
            showDetails={false}
          />

          {/* Links */}
          <div className="flex items-center justify-between text-sm">
            <Link
              to="/forgot-password"
              className="text-blue-600 hover:text-blue-500 transition-colors"
            >
              Esqueceu a senha?
            </Link>
            <Link
              to="/register"
              className="text-blue-600 hover:text-blue-500 transition-colors"
            >
              Criar conta
            </Link>
          </div>
        </form>

        {/* Demo Accounts */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg relative">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Contas de Demonstração:</h3>
          <div className="space-y-2 text-xs text-gray-600">
            <div>
              <strong>Paciente:</strong> paciente@demo.com / demo123
            </div>
            <div>
              <strong>Médico:</strong> medico@demo.com / demo123
            </div>
          </div>
          
          {/* Clear Storage Button */}
          <button
            onClick={handleClearStorage}
            className="absolute top-2 right-2 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors group"
            title="Limpar armazenamento local (localStorage)"
          >
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Limpar armazenamento local</span>
          </button>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500">
          <p>
            Ao fazer login, você concorda com nossos{' '}
            <Link to="/terms" className="text-blue-600 hover:text-blue-500">
              Termos de Uso
            </Link>{' '}
            e{' '}
            <Link to="/privacy" className="text-blue-600 hover:text-blue-500">
              Política de Privacidade
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}